<template>
  <div class="w-full p-2 border border-grey rounded-md">
    <div class="flex items-center justify-between">
      <!-- Profile Section -->
      <div class="flex items-center space-x-4">
        <div class="w-16 h-16 bg-light-apricot rounded-full flex items-center justify-center overflow-hidden">
          <!-- Show actual image if photo exists -->
          <img
              v-if="expertUser?.photo && expertUser.photo !== null && expertUser.photo !== ''"
              :src="getValidProfileURL(expertUser.photo)"
              alt="Profile"
              class="w-full h-full object-cover"
              @error="handleImgError"
          />
          <!-- Show initials avatar if no photo -->
          <div
              v-else
              :class="[
                'w-full h-full rounded-full flex items-center justify-center text-white font-semibold text-h3',
                userAvatarInfo.bgColor
              ]"
          >
            {{ userAvatarInfo.initials }}
          </div>
        </div>

        <!-- Profile Info -->
        <div>
          <h3 class="text-h2 font-semibold">{{ displayName }}</h3>
          <button disabled class="text-h4 text-light-blue font-normal disabled:cursor-not-allowed disabled:text-gray-500 disabled:opacity-50 disabled:hover:text-gray-500">
            Preview public profile
          </button>
        </div>
      </div>

      <!-- Arrow Icon -->
      <div class="m-8 cursor-pointer" @click="$emit('toggle-expand')">
        <RightArrow />
      </div>
    </div>

    <!-- User Information -->
    <div class="space-y-2 mt-4" v-if="expandAccountInfo">
      <div class="flex justify-between items-center">
        <span class="text-h4 font-normal text-grey-dark">Account Type:</span>
        <span class="text-h4 font-normal text-primary">{{ accountType }}</span>
      </div>

      <div class="flex justify-between items-center">
        <span class="text-h4 font-normal text-grey-dark">Current Plan:</span>
        <span class="text-h4 font-normal text-primary">{{ currentPlan }}</span>
      </div>

      <div class="flex justify-between items-center">
        <span class="text-h4 font-normal text-grey-dark">Next Billing Date:</span>
        <span class="text-h4 font-normal text-primary">{{ nextBillingDate }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { handleImgError, generateInitialsAvatar } from "@/utils/helpers.ts"
import { formatDate } from '@/utils/date.ts'
import RightArrow from '@/assets/icons/right-arrow.svg'
import { getValidProfileURL } from '@/pages/expert/listing/utils/getValidProfileURL'

const props = defineProps<{
  expertUser: any
  expandAccountInfo?: boolean
}>()

defineEmits<{
  'toggle-expand': []
}>()

const displayName = computed(() => {
  const user = props.expertUser
  if (!user) return 'User'
  
  const firstName = user.first_name || ''
  const lastName = user.last_name || ''
  
  if (firstName && lastName) {
    // Show first name and last initial
    return `${firstName} ${lastName.charAt(0)}.`
  } else if (firstName) {
    return firstName
  } else if (lastName) {
    return lastName
  }
  return 'User'
})

const userAvatarInfo = computed(() => {
  const user = props.expertUser
  const name = user ? `${user.first_name || ''} ${user.last_name || ''}`.trim() : ''
  return generateInitialsAvatar(name || 'User')
})

const accountType = computed(() => {
  const expertPlan = props.expertUser?.expert_plan;
  if (!expertPlan?.account_type) return 'N/A'
  
  // Capitalize first letter of expert_type
  return expertPlan.account_type.charAt(0).toUpperCase() + expertPlan.account_type.slice(1)
})

const currentPlan = computed(() => {
  const expertPlan = props.expertUser?.expert_plan;
  if (!expertPlan) return 'N/A'
  
  return expertPlan?.plan?.name
})

const nextBillingDate = computed(() => {
  const expertPlan = props.expertUser?.expert_plan;
  if (!expertPlan) return 'N/A'
  const date = expertPlan.next_bill_date;
  const formattedDate = formatDate(date);
  
  return expertPlan?.next_bill_date !== null ? formattedDate : 'N/A'
})
</script>
